import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  DeleteSweep as DeleteSweepIcon,
  Storefront as StorefrontIcon,
  WarningAmberRounded as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import BreakGlassDialog from 'components/BreakGlass/BreakGlassDialog';
import { getLoggedInUserDetails, sendBreakGlassLog } from 'utils/breakGlass';

export const ACCENT = '#FF8A00';
const DEFAULT_PROVIDERS = ['Clover', 'Square', 'Toast'];

/**
 * Shared chrome for the menu import screens: page header, the two-column grid,
 * the break-glass protected "Empty menu" card, the POS provider teaser and the
 * success dialog. The import mechanism itself is supplied as the left-hand card
 * so that the JSON and AI tabs cannot drift apart visually.
 *
 * `children` is a render prop receiving `{ disabled }`, which is true while
 * either the caller's own work (`busy`) or an empty-menu run is in flight.
 * `secondaryCard` is an optional render prop with the same signature; when
 * supplied it is placed between the import card and the "Empty menu" card and
 * the grid switches from two to three columns.
 *
 * @param {string} businessUuid
 * @param {string} breakGlassPage              break-glass log "page" identifier
 * @param {string} breakGlassEventType         break-glass log event type
 * @param {string} emptyActionSummary          human readable action summary
 * @param {() => Promise} onEmpty              throws on failure
 * @param {boolean} busy                       caller has work in flight
 * @param {string} successMessage              shown in the success dialog
 * @param {(message: string) => void} onSuccess       raised when emptying succeeds
 * @param {() => void} onSuccessClose                 dismisses the success dialog
 * @param {({ disabled }) => ReactNode} [secondaryCard]  optional middle card
 */
const ImportMenuLayout = ({
  businessUuid,
  breakGlassPage,
  breakGlassEventType,
  emptyActionSummary,
  emptySuccessMessage = 'Menu emptied successfully.',
  onEmpty,
  busy = false,
  successMessage = '',
  onSuccess,
  onSuccessClose,
  title,
  subtitle,
  comingSoonProviders = DEFAULT_PROVIDERS,
  secondaryCard,
  children,
}) => {
  const { t } = useTranslation();
  const loggedInUser = useSelector(({ user }) => user.data);
  const loggedInUserDetails = getLoggedInUserDetails(loggedInUser);

  const [emptying, setEmptying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [breakGlassOpen, setBreakGlassOpen] = useState(false);

  const disabled = busy || emptying;
  const cardColumns = secondaryCard ? 4 : 6;

  const ensureBusiness = () => {
    if (!businessUuid) {
      toast.error(t('No business selected'));
      return false;
    }
    return true;
  };

  const handleEmptyClick = () => {
    if (!ensureBusiness()) return;
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  const handleConfirmEmpty = () => {
    setConfirmOpen(false);
    setBreakGlassOpen(true);
  };

  const handleAuthorized = (employeeName) => {
    if (!ensureBusiness()) return;
    setEmptying(true);
    Promise.resolve(onEmpty())
      .then(() => {
        sendBreakGlassLog(
          {
            eventType: breakGlassEventType,
            employeeName,
            actionSummary: emptyActionSummary,
            result: 'success',
            loggedInUser: loggedInUserDetails,
          },
          breakGlassPage
        );
        onSuccess(t(emptySuccessMessage));
      })
      .catch(() => {
        sendBreakGlassLog(
          {
            eventType: breakGlassEventType,
            employeeName,
            actionSummary: emptyActionSummary,
            result: 'failed',
            loggedInUser: loggedInUserDetails,
          },
          breakGlassPage
        );
      })
      .finally(() => {
        setEmptying(false);
      });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1F2733' }}>
          {t(title)}
        </Typography>
        <Typography variant="body1" sx={{ color: '#6B7280', mt: 0.5 }}>
          {t(subtitle)}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Import mechanism (JSON upload or AI generation) */}
        <Grid item xs={12} md={cardColumns}>
          {children({ disabled })}
        </Grid>

        {secondaryCard && (
          <Grid item xs={12} md={cardColumns}>
            {secondaryCard({ disabled })}
          </Grid>
        )}

        {/* Empty menu (danger zone) */}
        <Grid item xs={12} md={cardColumns}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              border: '1px solid #FCA5A5',
              borderRadius: 3,
              backgroundColor: 'rgba(239, 68, 68, 0.02)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(239, 68, 68, 0.12)',
                    color: '#DC2626',
                  }}
                >
                  <WarningIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Empty menu')}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: '#6B7280', mb: 2.5, minHeight: 40 }}
              >
                {t(
                  'Remove all menu data for this business. This action requires break-glass authorization and cannot be undone.'
                )}
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleEmptyClick}
                disabled={disabled || !businessUuid}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {t('Empty menu')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Import from POS providers */}
        {comingSoonProviders.length > 0 && (
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{ border: '1px solid #E5E7EB', borderRadius: 3 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <StorefrontIcon sx={{ color: '#6B7280' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('Import from POS provider')}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                {t('Direct provider integrations are coming soon.')}
              </Typography>
              <Divider sx={{ my: 2.5 }} />
              <Grid container spacing={2}>
                {comingSoonProviders.map((provider) => (
                  <Grid item xs={12} sm={4} key={provider}>
                    <Box
                      sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#F9FAFB',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: '#374151' }}
                      >
                        {t(`Import from ${provider}`)}
                      </Typography>
                      <Chip
                        label={t('Soon')}
                        size="small"
                        sx={{
                          backgroundColor: '#E5E7EB',
                          color: '#6B7280',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('Confirm Empty Menu')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('This will remove all menu data for this business.')}
          </DialogContentText>
          <DialogContentText>
            {t(
              'We will record this action and send the information to management. Are you sure you want to do that?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleCloseConfirm} sx={{ textTransform: 'none' }}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disableElevation
            onClick={handleConfirmEmpty}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <BreakGlassDialog
        open={breakGlassOpen}
        onClose={() => setBreakGlassOpen(false)}
        onAuthorized={handleAuthorized}
        page={breakGlassPage}
        actionSummary={emptyActionSummary}
        description="Enter the employee name and break-glass code to authorize emptying the menu."
      />

      <Dialog
        open={Boolean(successMessage)}
        onClose={onSuccessClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(34, 197, 94, 0.12)',
              color: '#16A34A',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {t('Success')}
          </Typography>
          <DialogContentText sx={{ color: '#6B7280' }}>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={onSuccessClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              backgroundColor: ACCENT,
              '&:hover': { backgroundColor: '#E67C00' },
            }}
          >
            {t('Done')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportMenuLayout;
